// Object to map attributes to corresponding classes and animation durations (in milliseconds)
const animationMap = {
  'data-fade-up': { class: 'faded-up', duration: 1200, threshold: 0 },
  // 'data-button-animate-in': { class: 'animated-in', duration: 100, threshold: 0 },
  // 'data-image-animate-in': { class: 'animated-in', duration: 1500, threshold: 0 },
  // 'data-block-animate-in': { class: 'animated-in', duration: 1000, threshold: 0 },
  // 'data-fade-text': { class: 'faded-up', duration: 0, threshold: 0 },
};

// Queue to hold elements that are ready for animation
let animationQueue = [];
let isProcessingQueue = false; // Flag to check if the queue is currently being processed

// Default delay between each element in the queue (in milliseconds)
const defaultQueueDelay = 500;

// Function to add elements to the queue and start processing
const addToQueue = (element, className, duration, delay = defaultQueueDelay) => {
  // Check if the element is already in the queue
  if (!animationQueue.some(item => item.element === element && item.className === className)) {
    animationQueue.push({ element, className, duration, delay });
    if (!isProcessingQueue) {
      processQueue();
    }
  }
};

// Function to process the queue with a dynamic delay based on visibility
const processQueue = () => {
  if (animationQueue.length === 0) {
    isProcessingQueue = false;
    return;
  }

  isProcessingQueue = true;

  const { element, className, duration, delay } = animationQueue.shift(); // Get the first element in the queue
  if (delay !== 0) {
    // console.log(element);
    // console.log({ element, className, duration, delay })
  }

  // Check if the element is in the viewport
  const rect = element.getBoundingClientRect();
  const isInViewport = rect.top >= 0;

  // If the element is not in the viewport, skip the delay
  const effectiveDelay = isInViewport ? delay : 0;

  element.classList.add(className); // Apply the class to trigger animation

  // Set a timeout to remove the class and data attribute after duration + effectiveDelay
  if (duration > 0) {
    setTimeout(() => {
      element.classList.remove(className); // Remove the animation class
      removeDataAttributes(element); // Remove data attributes associated with animation
    }, duration + effectiveDelay);
  }

  // Process the next element in the queue after the effective delay
  setTimeout(() => {
    processQueue();
  }, effectiveDelay);
};


// Helper function to remove data attributes after animation completes
const removeDataAttributes = (element) => {
  Object.keys(animationMap).forEach(attr => {
    if (element.hasAttribute(attr)) {
      element.removeAttribute(attr); // Remove the data attribute
    }
  });
};

// Function to apply animation to an element, accepting a delay as a parameter
const applyAnimation = (element, delay) => {
  Object.keys(animationMap).forEach(attr => {
    if (element.hasAttribute(attr)) {
      const { class: className, duration } = animationMap[attr];
      
      if (attr === 'data-fade-text') {
        // For 'data-fade-text', animate all text elements inside
        const elements = element.querySelectorAll('.display-l,.display-m,.display-s,.heading-xl,.heading-l,.heading-m,.heading-s');
        elements.forEach((textElement) => {
          if (!textElement.classList.contains(className)) {
            addToQueue(textElement, className, duration, delay);
          }
        });
      } else {
        // Add the element to the queue for other animations
        if (!element.classList.contains(className)) {
          addToQueue(element, className, duration, delay);
        }
      }
    }
  });
};

// Updated handleIntersection to include elements above the scroll point
const handleIntersection = (entries, observer) => {
  entries.forEach((entry) => {
    const threshold = getThresholdForElement(entry.target); // Get threshold from the animationMap
    const isCorrectThreshold = entry.intersectionRatio >= threshold; // Check if element meets its threshold

    if (entry.isIntersecting && isCorrectThreshold) {
      // Apply animation using the custom delay if provided, otherwise use defaultQueueDelay
      const customDelay = parseInt(entry.target.getAttribute('data-delay')) || defaultQueueDelay;
      applyAnimation(entry.target, customDelay);
    }
  });
};

// Function to animate elements that are above the viewport (already scrolled past)
const animateElementsAboveViewport = () => {
  // Get all elements that have attributes in the animationMap
  const allAnimatedElements = [];
  Object.keys(animationMap).forEach(attr => {
    const elements = document.querySelectorAll(`[${attr}]`);
    elements.forEach(el => {
      allAnimatedElements.push(el);
    });
  });

  // Loop through the elements and check if they are above the viewport
  allAnimatedElements.forEach(element => {
    const rect = element.getBoundingClientRect();
    if (rect.bottom <= 0) {
      // Element is above the viewport, apply animation with 0 delay
      applyAnimation(element, 0); // Pass 0 delay for elements above the viewport
    }
  });
};

// Function to create an observer with a default threshold (can adjust in handleIntersection)
const createObserver = () => {
  return new IntersectionObserver(handleIntersection, {
    root: null,
    rootMargin: calculateRootMargin(), // Trigger when the element is 20% into the viewport
    threshold: [0, 0.3] // We'll handle specific threshold values inside handleIntersection
  });
};

// Function to determine the threshold from animationMap for an element
const getThresholdForElement = (element) => {
  for (let attr in animationMap) {
    if (element.hasAttribute(attr)) {
      return animationMap[attr].threshold; // Return the threshold from animationMap
    }
  }
  return 0; // Default to 0 if no specific attribute is found
};

// Function to observe elements based on their attributes in DOM order
export const observeElements = () => {
  const observer = createObserver(); // Create the observer once at the start

  // Get all elements that have attributes in the animationMap
  const allAnimatedElements = [];
  Object.keys(animationMap).forEach(attr => {
    const elements = document.querySelectorAll(`[${attr}]`);
    elements.forEach(el => {
      allAnimatedElements.push(el);
    });
  });

  // Sort the elements based on their position in the DOM
  const sortedElements = Array.from(allAnimatedElements).sort((a, b) => {
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  });

  // Observe the sorted elements
  sortedElements.forEach(el => {
    observer.observe(el); // Only observe the elements, thresholds handled in the callback
  });
};

// Function to calculate rootMargin, using 0.2 for desktop and 0.1 for mobile
const calculateRootMargin = () => {
  const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
  
  // Define the breakpoint for mobile, e.g., 767px
  const isMobile = window.innerWidth <= 767;

  // For desktop, use 0.1 margin; for mobile, use 0
  const marginPercentage = 0;
  const marginValue = viewportHeight * marginPercentage;

  return `0px 0px -${marginValue}px 0px`;
};

// Initialize everything after DOM is fully loaded
document.addEventListener("DOMContentLoaded", () => {
  // First, animate elements above the viewport
  animateElementsAboveViewport();

  // Then, observe elements for future intersection
  observeElements();
});

// Parallax function now queries the DOM fresh each time.
const updateParallax = () => {
  const parallaxElements = document.querySelectorAll("[data-parallax-effect]");
  const windowHeight = window.innerHeight;
  parallaxElements.forEach(el => {
    const rect = el.getBoundingClientRect();
    // Calculate progress: 0 when the element's top is at the bottom of the viewport,
    // and 1 when the element's bottom is at the top, then remap to a range of -1 to 1.
    let progress01 = (windowHeight - rect.top) / (windowHeight + rect.height);
    progress01 = Math.min(Math.max(progress01, 0), 1);
    const progress = progress01 * 2 - 1;
    el.style.setProperty("--progress", progress);

    // Get the movement value from the data attribute.
    const movementAttr = el.getAttribute("data-parallax-movement") || "0";
    // Append "%" if it is a numeric value.
    const movementValue = isNaN(Number(movementAttr)) ? movementAttr : movementAttr + "%";
    el.style.setProperty("--movement", movementValue);
  });
};

// Initial parallax calculation.
updateParallax();
window.addEventListener("scroll", updateParallax);
window.addEventListener("resize", updateParallax);